/**
 * @module MouseHandler
 * @description A component that wraps the UI to handle all mouse interactions in the application.
 * It manages mouse events, dispatches actions, and renders measurement tools based on the current action.
 */

import React, { useState, useEffect, useContext, ReactNode } from "react";
import styled from "styled-components";
import { useSelector, useDispatch, useStore } from "react-redux";
import useMousePosition from "hooks/useMousePosition";
import { useAlert } from "hooks/useAlert";
import { evaluateTileDrop } from "reduxModules/calculations/roomCalculations";
import MuiMeasuringTape from "components/elements/MuiMeasuringTape";
import { getDrawingBox, getCurrentDevice } from "reduxModules/ducks/ui";
import ReducersIndex from "reduxModules/ducks";
import ToolContext, { ToolContextType, ActionDispatcher } from 'components/services/ToolContext';
import MuiAudioPlayer from "components/elements/MuiAudioPlayer";
import MuiPositionDisplay from "components/elements/MuiPositionDisplay";
import MuiDistanceDisplay from "components/elements/MuiDistanceDisplay";
import MuiPlotLevelDisplay from "components/elements/MuiPlotLevelDisplay";
import { getCurrentValue2, getPlotData, getPlotProps } from "reduxModules/selectors/containerSelectors";
import { values } from "lodash";
import { bilinearInterpolate } from "utilities/mathematic";
import { sortDevicesByColumnThenRow } from "utilities/geometric";

/**
 * Interface for mouse coordinates
 */
interface MousePosition {
  x: number;
  y: number;
}

/**
 * Interface for drawing box dimensions
 */
interface DrawingBox {
  bbox: {
    x0: number;
    y0: number;
    x1: number;
    y1: number;
    cx: number;
    cy: number;
    width: number;
    height: number;
  };
  zoom: boolean;
  scale: number;
}

/**
 * Interface for the current device
 */
interface Device {
  id: string;
  type: string;
  [key: string]: any;
}

// Using ActionDispatcher from ToolContext.tsx instead of redefining it here

/**
 * Interface for MouseHandler props
 */
interface MouseHandlerProps {
  children: ReactNode;
}

/**
 * Interface for action props passed to tool components
 */
interface ActionProps {
  action?: string;
  anchor: MousePosition;
  target: MousePosition;
  mouseActive: boolean;
}

/**
 * Interface for the wrapper styled component
 */
interface WrapperProps {
  pointer?: boolean;
  onMouseDown: (evt: React.MouseEvent) => void;
  onMouseMove: (evt: React.MouseEvent) => void;
  onMouseUp: (evt: React.MouseEvent) => void;
}

/**
 * A highlightable device: its room coordinates plus the exact id MuiDevices renders for it.
 */
interface DeviceCandidate {
  id: string;
  x: number;
  y: number;
}

/**
 * Build the list of highlightable devices for the distance action, each carrying the SAME id
 * MuiDevices renders for that icon — that id is what setSelectedDevice matches to apply the
 * red-fill highlight, so it must line up exactly.
 *
 * Mirrors MuiMicrophones' render grouping (see elements/MuiDevices.tsx): ceiling expansion mics
 * (dcm_x / legacy tcm_x) are mirrored into microphones.layout but, when expansionMicType is
 * 'ceiling', they are filtered OUT of the primary render group (which then re-indexes over the
 * filtered list) and drawn under the dedicated `expansionMicrophones` group. Table expansion mics
 * (ttm_x) live only on expansionMicrophones.layout. Both expansion kinds therefore resolve to
 * `${area.id}-expansion-expansionMicrophones-${index}`, not the primary `${area.id}-microphones-${index}`.
 */
export const buildDeviceCandidates = (area: any, deviceType: string | null): DeviceCandidate[] => {
  const candidates: DeviceCandidate[] = [];
  if (!area || !deviceType) return candidates;

  const primaryLayout: any[] = area[deviceType]?.layout ?? [];

  if (deviceType === 'microphones') {
    const isCeilingExpansionModel = (m: string) => m === 'dcm_x' || m === 'tcm_x';
    const hasCeilingExpInLayout = area.expansionMicType === 'ceiling'
      && primaryLayout.some((m: any) => isCeilingExpansionModel(m.model));
    const renderedPrimary = hasCeilingExpInLayout
      ? primaryLayout.filter((m: any) => !isCeilingExpansionModel(m.model))
      : primaryLayout;

    renderedPrimary.forEach((d: any, i: number) => {
      candidates.push({ id: `${area.id}-microphones-${i}`, x: d.location.x, y: d.location.y });
    });

    const expansionLayout: any[] = area.expansionMicrophones?.layout ?? [];
    expansionLayout.forEach((d: any, i: number) => {
      candidates.push({ id: `${area.id}-expansion-expansionMicrophones-${i}`, x: d.location.x, y: d.location.y });
    });
  } else {
    primaryLayout.forEach((d: any, i: number) => {
      candidates.push({ id: `${area.id}-${deviceType}-${i}`, x: d.location.x, y: d.location.y });
    });
  }

  return candidates;
};

/**
 * MouseHandler component
 *
 * @param props - Component props
 * @returns Wrapped UI with mouse event handling
 */
const MouseHandler: React.FC<MouseHandlerProps> = ({ children }) => {
  // Access Redux state
  const currentAction = useSelector((state: any) => state.ui.action) as string | undefined;
  const drawingBox = useSelector((state: any) => getDrawingBox(state)) as DrawingBox;
  // getCurrentDevice returns a device type string like 'microphones', 'speakers', etc.
  const currentDevice = useSelector((state: any) => getCurrentDevice(state)) as string | null;
  const aimpointDragging = useSelector((state: any) => state.ui.aimpointDragging) as boolean;

  // Local state
  const [action, setAction] = useState<string | undefined>();
  const [mouseActive, setMouseActive] = useState<boolean>(false);
  const [anchor, setAnchor] = useState<MousePosition>({ x: 0, y: 0 });
  const [target, setTarget] = useState<MousePosition>({ x: 0, y: 0 });
  
  // State for new UI components
  const [hoveredDevice, setHoveredDevice] = useState<any>(null);
  const [closestDevice, setClosestDevice] = useState<any>(null);
  const [plotLevelData, setPlotLevelData] = useState<any>(null);
  
  // Get mouse position from custom hook
  const mouse = useMousePosition() as MousePosition | null;

  const dispatch = useDispatch();

  // Get area data for device detection
  const areas = useSelector((state: any) => state.container.areas) as any;
  const activeTab = useSelector((state: any) => state.container.activeTab) as string;
  const area = areas && activeTab ? areas[activeTab] : null;

  // Utility function to find devices near mouse position
  const findDeviceNearMouse = (mousePos: MousePosition): any => {
    if (!area || !mousePos || !currentDevice) return null;
    
    const { bbox, scale } = drawingBox;
    const threshold = 30; // pixels
    
    // Convert mouse position to room coordinates
    const roomX = (mousePos.x - bbox.x0) / scale;
    const roomY = (mousePos.y - bbox.y0) / scale;
    
    // Only check devices of the current device type
    const deviceType = currentDevice;
    const devices = area[deviceType];

    // Microphone hovers also need to surface expansion mics. Table expansion mics live
    // exclusively on area.expansionMicrophones.layout (not merged into microphones.layout),
    // so without folding them in here they'd be undetectable. Ceiling expansion mics are
    // already in area.microphones.layout via getDeviceLayouts' allMicCoords, so we dedupe
    // by (model, x, y) when merging to avoid the same device showing up twice.
    //
    // Speaker hovers do the same for expansion speakers, which live exclusively on
    // area.expansionSpeakers.layout. They're tagged so the deviceInfo below uses their own
    // model/height/mounting rather than the primary (bar) speaker's.
    const baseLayout: any[] = (devices && devices.layout) ? devices.layout : [];
    const expansionMicLayout: any[] = deviceType === 'microphones'
      ? (area.expansionMicrophones?.layout ?? [])
      : [];
    const expansionSpeakerLayout: any[] = deviceType === 'speakers'
      ? (area.expansionSpeakers?.layout ?? []).map((d: any) => ({ ...d, _isExpansionSpeaker: true }))
      : [];
    const seen = new Set<string>();
    const dedupKey = (d: any) => `${d.model}|${d.location?.x}|${d.location?.y}`;
    const combinedLayout = [...baseLayout, ...expansionMicLayout, ...expansionSpeakerLayout].filter((d: any) => {
      const k = dedupKey(d);
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });

    if (combinedLayout.length > 0) {
      // Order devices for the tooltip's display number. This MUST match the numbering in the
      // Equipment list's Microphone locations tab (getPositionDetail, containerSelectors.ts), which
      // orders by column then top-to-bottom within a column — otherwise hovering the mic listed as
      // row #2 shows "Microphone 3".
      //
      // Was `.sort((a, b) => a.device.location.x - b.device.location.x)` — x only. On a
      // keystone/vshape table the expansion mics are placed in mirrored pairs at the SAME x, so
      // every pair tied on the sort key and its order fell through to the stored array order (which
      // is #micPath traversal order, bottom-of-pair first). Worse, path sampling leaves a ~1e-12
      // residue on x, so the tie wasn't even a real tie and the winner was decided by float noise.
      // `sortDevicesByColumnThenRow` groups near-equal x into one column (tolerance covers both the
      // float residue and the table's display rounding) and orders each column by y.
      //
      // The wrapper carries `location` so the shared helper can read it, and `originalIndex` so the
      // caller still knows the device's position in `combinedLayout`.
      const sortedDevices = sortDevicesByColumnThenRow(
        combinedLayout.map((device: any, index: number) => ({
          device,
          originalIndex: index,
          location: device.location,
        })),
      );

      for (let sortedIndex = 0; sortedIndex < sortedDevices.length; sortedIndex++) {
        const { device, originalIndex: i } = sortedDevices[sortedIndex];
        const deviceX = device.location.x;
        const deviceY = device.location.y;
        const deviceZ = device.location.z; // Extract Z coordinate from location

        // Convert device position to screen coordinates
        const screenX = bbox.x0 + deviceX * scale;
        const screenY = bbox.y0 + deviceY * scale;

        // Check if mouse is within threshold
        const distance = Math.sqrt(
          Math.pow(mousePos.x - screenX, 2) + Math.pow(mousePos.y - screenY, 2)
        );

        if (distance < threshold) {
          const isExpansionSpeaker = Boolean(device._isExpansionSpeaker);
          let modelName = device.modelName || device.model || 'Unknown Model';

          // Only inherit the primary-mic modelName when this entry IS the primary mic
          // (matching model keys). For expansion mics (dcm_x ceiling / ttm_x table) the
          // entry already has its own modelName (Parlé DCM-X / DTM-X) — don't clobber it.
          if (deviceType === 'microphones' && device.model === area.microphones?.model) {
            modelName = area.microphones?.modelName || modelName;
          }
          
          // For speakers, check if this is a bar system
          const isMicrophoneBar = area.microphones && (area.microphones.model === 'bar' || area.microphones.model === 'videobar' || area.microphones.model === 'audiobar');
          const isSpeakerBar = area.speakers && area.speakers.model === 'barspkr';
          
          if (deviceType === 'speakers' && !isExpansionSpeaker && (isSpeakerBar || isMicrophoneBar)) {
            // Use the selected microphone model name for bar speakers. Expansion speakers are
            // standalone (e.g. Desono P6-SM) — keep their own model, not the bar mic's name.
            modelName = area.microphones?.modelName || area.microphones?.model || 'Parlé VBC 2500a';
          }
          
          // For cameras, check if this is a bar system
          if (deviceType === 'cameras') {
            if (isMicrophoneBar) {
              // For bar systems, look up the actual camera model being used
              const cameraModel = area.cameras?.model;
              if (cameraModel && toolContext?.constants?.equipment?.cameras?.[cameraModel]) {
                modelName = toolContext.constants.equipment.cameras[cameraModel].modelName || modelName;
              } else {
                // Fallback to microphone model name for VBC systems
                const micModelName = area.microphones?.modelName || area.microphones?.model || '';
                if (!micModelName.includes('ABC')) {
                  modelName = micModelName || 'Parlé VBC 2500a';
                }
              }
            } else {
              // For standalone cameras, look up the proper model name from constants
              const cameraModel = area.cameras?.model;
              if (cameraModel && toolContext?.constants?.equipment?.cameras?.[cameraModel]) {
                modelName = toolContext.constants.equipment.cameras[cameraModel].modelName || modelName;
              }
            }
          }
          
          // `sortedIndex` is the device's rank in column-then-row order (see the sort above), so the
          // 1-based number MuiPositionDisplay derives from this id matches the row number the
          // Microphone locations tab shows for the same device.
          const deviceInfo: any = {
            type: deviceType,
            position: {
              x: deviceX,
              y: deviceY,
              z: deviceZ // Include Z coordinate in position object
            },
            id: `${area.id}-${deviceType}-${sortedIndex}`,
            screenPosition: { x: screenX, y: screenY },
            model: modelName,
            height: device.height, // Keep for backward compatibility
            originalIndex: i
          };

          // For speakers, include height and angles if available
          if (deviceType === 'speakers' && device) {
            // Bar and expansion speakers store their mounting height on location.z, which already
            // shows as Z in the X/Y/Z line — don't also emit a duplicate Height line for them.
            // Ceiling/pendant/wall speakers keep the Height line from their own `height`.
            const isBarSpeaker = isSpeakerBar || isMicrophoneBar;
            if (!isExpansionSpeaker && !isBarSpeaker && device.height !== undefined) {
              deviceInfo.height = device.height;
            }
            if (device.horAngle !== undefined) {
              deviceInfo.horAngle = device.horAngle;
            }
            if (device.verAngle !== undefined) {
              deviceInfo.verAngle = device.verAngle;
            }
            // Include mounting type to determine if it's a wall speaker. Expansion speakers use
            // their own mounting (expansionSpeakerType), not the primary/bar speaker's.
            deviceInfo.mountingType = isExpansionSpeaker
              ? (area.expansionSpeakerType || 'ceiling')
              : (area.speakers?.mountingType || 'ceiling');
          } else if (deviceType === 'cameras' && device) {
            // For cameras, include horizontal angle if available.
            // All cameras (bar and standalone) use rotation convention (0 = facing right).
            if (device.horAngle !== undefined) {
              deviceInfo.horAngle = device.horAngle;
            }
          }

          return deviceInfo;
        }
      }
    }
    
    return null;
  };

  // Utility function to find closest device
  const findClosestDevice = (mousePos: MousePosition): any => {
    if (!area || !mousePos || !currentDevice) return null;

    const { bbox, scale } = drawingBox;

    // Convert mouse position to room coordinates
    const roomX = (mousePos.x - bbox.x0) / scale;
    const roomY = (mousePos.y - bbox.y0) / scale;

    let closestDevice = null;
    let minDistance = Infinity;

    // buildDeviceCandidates folds in expansion mics and emits render-matching ids.
    for (const candidate of buildDeviceCandidates(area, currentDevice)) {
      const distance = Math.sqrt(
        Math.pow(roomX - candidate.x, 2) + Math.pow(roomY - candidate.y, 2)
      );

      if (distance < minDistance) {
        minDistance = distance;
        closestDevice = {
          // Keep the device-menu type so MuiDistanceDisplay still labels it a "microphone".
          type: currentDevice,
          position: { x: candidate.x, y: candidate.y },
          id: candidate.id,
          distance: distance
        };
      }
    }

    return closestDevice;
  };

  // Get the current plot type and plot data from Redux
  const plotProps = useSelector(getPlotProps) as any;
  const plotData = useSelector(getPlotData);
  const plotType = plotProps?.plotType as string | undefined;

  // Utility function to get plot level data from actual plot calculations
  const getPlotLevelData = (mousePos: MousePosition): any => {
    if (!mousePos) return null;
    
    const { bbox, scale } = drawingBox;
    
    // Convert mouse position to room coordinates
    const roomX = (mousePos.x - bbox.x0) / scale;
    const roomY = (mousePos.y - bbox.y0) / scale;
    const point = { x: roomX, y: roomY };
    
    // Use the actual plot data that's already been processed for the heat map
    if (plotData?.ready && plotData?.areaArray?.length > 0) {
      const areaData = plotData.areaArray[0]?.areaData?.[0]?.data;
      
      if (areaData?.mapArray) {
        const { mapArray, receiverSpacing, receiverXOffset, receiverYOffset, averageTarget } = areaData;
        
        // Use bilinearInterpolate to get the value at the mouse position
        const value = bilinearInterpolate(point, mapArray, receiverSpacing, receiverXOffset, receiverYOffset);
        
        if (value === null || isNaN(value)) return null;
        
        // For relative average level, we need to subtract the average target
        if (plotType === 'relativeAverage') {
          // The mapArray contains absolute SPL values
          // We need to subtract averageTarget to get the relative value
          // This matches devicePlots.js: pointToPixelValue(point, data) - data.averageTarget
          if (averageTarget === undefined || averageTarget === null) {
            return null;
          }
          
          const relativeLevel = value - averageTarget;
          
          // Clamp to the expected range (-12 to 6 dB)
          const clampedLevel = Math.max(-12, Math.min(6, relativeLevel));
          
          return {
            level: clampedLevel,
            type: 'Relative Average Level',
            unit: 'dB'
          };
        } else {
          // For absolute SPL plots (direct or total)
          return {
            level: value,
            type: plotType === 'directSPL' ? 'Direct SPL' : plotType === 'totalSPL' ? 'Total SPL' : 'SPL',
            unit: 'dBA'
          };
        }
      }
    }
    
    // For SPL plots (direct or total), use actual plot data if available
    if (currentDevice === 'speakers' && plotData?.ready && plotData?.areaArray?.length > 0) {
      // Try to find the plot data for the current area
      const areaData = plotData.areaArray[0]?.areaData?.[0]?.data;
      
      if (areaData?.mapArray) {
        const { mapArray, receiverSpacing, receiverXOffset, receiverYOffset } = areaData;
        const splValue = bilinearInterpolate(point, mapArray, receiverSpacing, receiverXOffset, receiverYOffset);
        
        if (splValue !== null && !isNaN(splValue)) {
          return {
            level: splValue,
            type: plotType === 'directSPL' ? 'Direct SPL' : 'Total SPL',
            unit: 'dBA'
          };
        }
      }
    }
    
    // Fallback for cases where real data isn't available
    // This is just for demo purposes
    if (currentDevice === 'speakers') {
      // Generate a simple gradient from center
      const centerX = (bbox.x1 - bbox.x0) / 2 / scale;
      const centerY = (bbox.y1 - bbox.y0) / 2 / scale;
      const distanceFromCenter = Math.sqrt(
        Math.pow(roomX - centerX, 2) + Math.pow(roomY - centerY, 2)
      );
      
      if (plotType === 'relativeAverage') {
        // For relative average demo, show values from +4 to -8 based on distance
        const relativeLevel = 4 - (distanceFromCenter * 0.8);
        const clampedLevel = Math.max(-12, Math.min(6, relativeLevel));
        return {
          level: clampedLevel,
          type: 'Relative Average Level',
          unit: 'dB'
        };
      } else {
        // For SPL demo, show absolute values
        const maxLevel = 85;
        const level = Math.max(30, maxLevel - (distanceFromCenter * 5));
        return {
          level: level,
          type: 'SPL',
          frequency: '1kHz',
          unit: 'dBA'
        };
      }
    }
    
    return null;
  };

  // Utility function to check if mouse is within floorplan bounds
  const isMouseInFloorplan = (mousePos: MousePosition): boolean => {
    if (!mousePos || !drawingBox) return false;
    
    const { bbox } = drawingBox;
    return (
      mousePos.x >= bbox.x0 &&
      mousePos.x <= bbox.x1 &&
      mousePos.y >= bbox.y0 &&
      mousePos.y <= bbox.y1
    );
  };

  // Handle mouse movement for different actions
  useEffect(() => {
    if (mouse && action && isMouseInFloorplan(mouse)) {
      switch (action) {
        case 'position':
          const deviceNearMouse = findDeviceNearMouse(mouse);
          setHoveredDevice(deviceNearMouse);
          break;
        case 'distance':
          const closest = findClosestDevice(mouse);
          setClosestDevice(closest);
          // Highlight the closest device so MuiDevices applies the selected (red-fill) state.
          dispatch(ReducersIndex.uiReducers.setSelectedDevice(closest?.id ?? null));
          break;
        case 'level':
          const plotData = getPlotLevelData(mouse);
          setPlotLevelData(plotData);
          break;
        default:
          setHoveredDevice(null);
          setClosestDevice(null);
          setPlotLevelData(null);
          dispatch(ReducersIndex.uiReducers.setSelectedDevice(null));
          break;
      }
    } else {
      setHoveredDevice(null);
      setClosestDevice(null);
      setPlotLevelData(null);
      dispatch(ReducersIndex.uiReducers.setSelectedDevice(null));
    }
  }, [mouse, action, area, drawingBox, currentDevice, dispatch]);

  // Update action when it changes in Redux; clear the highlight when leaving distance mode
  useEffect(() => {
    if (currentAction !== 'distance') {
      dispatch(ReducersIndex.uiReducers.setSelectedDevice(null));
    }
    setAction(currentAction);
    setMouseActive(false);
  }, [currentAction, dispatch]);

  // Get tool context with proper typing
  const toolContext = useContext(ToolContext) as ToolContextType;
  const actionDispatchers = toolContext.actionDispatchers;

  const store = useStore();
  const { showWarning } = useAlert();

  // Event handlers
  const handlers: WrapperProps = {
    onMouseDown: (evt: React.MouseEvent) => {
      if (mouse && action) {
        // Shared tile rules on ADD: a tile device (ceiling mic/speaker) may not be placed on a
        // blocked tile or a tile already holding a device — same `evaluateTileDrop` rules the
        // drag/move path uses. Block + warn instead of placing.
        if (action === 'add' && (currentDevice === 'microphones' || currentDevice === 'speakers')) {
          const state: any = store.getState();
          const area = state.container?.areas?.[state.container?.activeTab];
          const model = currentDevice === 'speakers' ? area?.speakers?.model : area?.microphones?.model;
          const box: any = drawingBox;
          const loc = {
            x: (mouse.x - box.bbox.x0) / box.scale,
            y: (mouse.y - box.bbox.y0) / box.scale,
          };
          const landing = evaluateTileDrop(state, loc, { deviceType: currentDevice, model });
          if (
            landing.outcome === 'dropped_on_occupied' ||
            landing.outcome === 'dropped_on_blocked' ||
            landing.outcome === 'all_blocked'
          ) {
            showWarning(
              landing.outcome === 'dropped_on_occupied'
                ? 'That tile already has a device — choose an empty tile.'
                : 'That ceiling tile is blocked — choose an unblocked tile.',
            );
            return; // skip the add
          }
        }
        if (action in actionDispatchers) {
          actionDispatchers[action](evt, dispatch, mouse, drawingBox, currentDevice);
        }
        setMouseActive(true);
        setAnchor(mouse);
        setTarget(mouse);
      }
    },
    onMouseMove: (e: React.MouseEvent) => {
      if (mouse) setTarget(mouse);
    },
    onMouseUp: (e: React.MouseEvent) => {
      setMouseActive(false);
    }
  };

  // Props for child components
  const props: ActionProps = {
    action,
    anchor,
    target,
    mouseActive
  };

  return (
    <Wrapper {...handlers} pointer={mouseActive && (action === 'measure')}>
      {children}
      {action === 'measure' && !aimpointDragging && <MuiMeasuringTape {...props} />}
      {action === 'listen' && <MuiAudioPlayer {...props} />}
      {action === 'position' && mouse && (
        <MuiPositionDisplay
          mousePosition={mouse}
          deviceInfo={hoveredDevice}
          visible={!!hoveredDevice}
        />
      )}
      {action === 'distance' && mouse && (
        <MuiDistanceDisplay
          mousePosition={mouse}
          closestDevice={closestDevice}
          visible={!!closestDevice}
        />
      )}
      {action === 'level' && mouse && (
        <MuiPlotLevelDisplay
          mousePosition={mouse}
          plotData={plotLevelData}
          visible={!!plotLevelData}
        />
      )}
    </Wrapper>
  );
};

/**
 * Styled wrapper for the MouseHandler
 */
const Wrapper = styled.g.attrs<WrapperProps>(props => ({
  className: 'MouseHandler'
}))<WrapperProps>`
  cursor: ${props => props.pointer ? 'pointer' : 'default'};
  /* fixme: could this be the problem? */
  pointer-events: bounding-box;
`;

// Attach Wrapper to MouseHandler for access in unit tests
(MouseHandler as any).Wrapper = Wrapper;

export default MouseHandler;
